
import React from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SubscriptionWallProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  title?: string;
  description?: string;
}

export const SubscriptionWall: React.FC<SubscriptionWallProps> = ({
  children,
  fallback,
  title = "Premium Feature",
  description = "This feature requires an active subscription. Subscribe now to unlock all premium features."
}) => {
  const { hasActiveSubscription, isLoading } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is a super user (Jake's email)
  const isSuperUser = user?.email === 'jake.pimental1@gmail.com';

  // If user has active subscription or is a super user, render the children
  if (hasActiveSubscription || isSuperUser) {
    return <>{children}</>;
  }

  // If loading, show a loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px] p-6 border rounded-lg bg-card text-card-foreground">
        <Loader2 className="h-6 w-6 animate-spin text-primary mb-2" />
        <p className="text-sm text-muted-foreground">Checking subscription status...</p>
      </div>
    );
  }

  // If not logged in, prompt to log in
  if (!user) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-muted-foreground" />
            Authentication Required
          </CardTitle>
          <CardDescription>
            Please log in to access this feature
          </CardDescription>
        </CardHeader>
        <CardFooter className="pt-4">
          <Button 
            className="w-full" 
            onClick={() => navigate('/auth', { state: { from: window.location.pathname } })}
          >
            Log In
          </Button>
        </CardFooter>
      </Card>
    );
  }

  // If custom fallback is provided, use that
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default subscription wall UI
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-muted-foreground" />
          {title}
        </CardTitle>
        <CardDescription>
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        <p>Upgrade to our Premium Plan to:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Access advanced analytics</li>
          <li>Get enhanced workflow tools</li>
          <li>Enjoy priority support</li>
        </ul>
      </CardContent>
      <CardFooter className="pt-4">
        <Button 
          className="w-full" 
          onClick={() => navigate('/settings', { state: { tab: 'subscription' } })}
        >
          <CreditCard className="h-4 w-4 mr-2" />
          Upgrade to Premium - $249/month
        </Button>
      </CardFooter>
    </Card>
  );
};

export default SubscriptionWall;
